<template>
  <div class="p-6">
    <!-- Header del Dashboard -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
      <p class="text-gray-600 dark:text-gray-400">Resumen de tu actividad en el sistema de impresión</p>
    </div>

    <!-- Sección de Mis Borradores -->
    <div class="mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow dark:shadow-lg">
        <div class="flex justify-between items-center mb-4">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Mis Borradores</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400">Cotizaciones sin finalizar</p>
          </div>
          <router-link
            to="/cotizaciones/nueva"
            class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors text-sm flex items-center gap-2"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            Nueva Cotización
          </router-link>
        </div>

        <!-- Loading -->
        <div v-if="loadingDrafts" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto"></div>
          <p class="text-sm text-gray-500 mt-2">Cargando borradores...</p>
        </div>

        <!-- Sin borradores -->
        <div v-else-if="drafts.length === 0" class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p class="mt-2 text-gray-500 dark:text-gray-400">No tienes borradores guardados</p>
          <p class="text-sm text-gray-400 dark:text-gray-500">Los borradores se guardan automáticamente mientras trabajas</p>
        </div>

        <!-- Lista de borradores -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="draft in drafts"
            :key="draft.id"
            class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            @click="$router.push(`/cotizaciones/${draft.id}/editar`)"
          >
            <div class="flex justify-between items-start mb-2">
              <h4 class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ draft.nombre }}</h4>
              <span class="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 text-xs rounded-full">
                Borrador
              </span>
            </div>

            <p class="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
              {{ draft.descripcion || 'Sin descripción' }}
            </p>

            <div class="text-xs text-gray-400 dark:text-gray-500 mb-2">
              <span v-if="draft.cliente">Cliente: {{ draft.cliente.nombre_completo }}</span>
              <span v-else>Sin cliente asignado</span>
            </div>

            <div class="flex justify-between items-center text-xs">
              <span class="text-gray-500 dark:text-gray-400">
                {{ draft.items_count || 0 }} items
              </span>
              <span class="text-gray-400 dark:text-gray-500">
                {{ formatDate(draft.updated_at) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Ver todos los borradores -->
        <div v-if="drafts.length > 0" class="mt-4 text-center">
          <router-link
            to="/cotizaciones?estado=borrador"
            class="text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium"
          >
            Ver todos los borradores →
          </router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp'

const { get, data, loading: loadingDrafts } = useHttp()
const drafts = ref([])

// Cargar borradores del usuario
const loadDrafts = async () => {
  try {
    await get('cotizaciones-my-drafts')
    if (data.value) {
      drafts.value = data.value
    }
  } catch (error) {
    console.error('Error al cargar borradores:', error)
    drafts.value = []
  }
}

// Formatear fecha
const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 1) return 'Hace 1 día'
  if (diffDays < 7) return `Hace ${diffDays} días`
  if (diffDays < 30) return `Hace ${Math.ceil(diffDays/7)} semanas`
  return date.toLocaleDateString()
}

onMounted(() => {
  loadDrafts()
})
</script>